import React, { useEffect, useState, useMemo } from 'react';
import { healthAPI } from '../api';
import { StatsResponse } from '../types';
import './Dashboard.css';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Line,
    LineChart,
    ComposedChart,
    Legend,
    ReferenceArea,
    ReferenceLine
} from 'recharts';

const calculateMovingAverage = <T extends Record<string, any>>(
    data: T[],
    index: number,
    window: number,
    key: keyof T
): number | null => {
    if (index < window - 1) return null;

    const sum = data
        .slice(index - window + 1, index + 1)
        .reduce((acc, item) => acc + (item[key] as number), 0);

    return sum / window;
};

export const calculatePeriodAverage = <T extends Record<string, any>>(
    data: T[],
    key: keyof T
): number => {
    const sum = data.reduce((acc, item) => acc + (item[key] as number), 0);
    return Math.round(sum / data.length);
};

export const processBristolTrend = (
    dailyStats: StatsResponse['daily_stats'],
    period: 'day' | 'week' | 'month'
): Array<{ date: string; dateFormatted: string; avg_bristol: number }> => {
    return dailyStats
        .filter((day): day is typeof day & { avg_bristol: number } =>
            day.avg_bristol !== undefined && day.avg_bristol > 0
        )
        .map(day => ({
            date: day.date,
            dateFormatted: new Date(day.date).toLocaleDateString('en-US', {
                weekday: period === 'week' ? 'short' : undefined,
                month: period === 'month' ? 'short' : undefined,
                day: 'numeric'
            }),
            avg_bristol: day.avg_bristol
        }));
};

export const processHealthScoreTrend = (
    dailyStats: StatsResponse['daily_stats'],
    period: 'day' | 'week' | 'month'
): Array<{
    date: string;
    dateFormatted: string;
    score: number;
    movingAvg: number | null;
}> => {
    return dailyStats.map((day, idx, arr) => ({
        date: day.date,
        dateFormatted: new Date(day.date).toLocaleDateString('en-US', {
            weekday: period === 'week' ? 'short' : undefined,
            month: period === 'month' ? 'short' : undefined,
            day: 'numeric'
        }),
        score: day.health_score_value,
        movingAvg: calculateMovingAverage(arr, idx, 7, 'health_score_value')
    }));
};

export const processTransitTimeDistribution = (
    distribution: Record<string, number> | undefined
): Array<{ range: string; count: number; min: number; max: number }> => {
    if (!distribution) return [];

    const buckets = [
        { range: '0-6h', min: 0, max: 6, count: distribution['0-6'] || 0 },
        { range: '6-12h', min: 6, max: 12, count: distribution['6-12'] || 0 },
        { range: '12-18h', min: 12, max: 18, count: distribution['12-18'] || 0 },
        { range: '18-24h', min: 18, max: 24, count: distribution['18-24'] || 0 },
        { range: '24-30h', min: 24, max: 30, count: distribution['24-30'] || 0 },
        { range: '30-36h', min: 30, max: 36, count: distribution['30-36'] || 0 },
        { range: '36-48h', min: 36, max: 48, count: distribution['36-48'] || 0 },
        { range: '48h+', min: 48, max: Infinity, count: distribution['48+'] || 0 }
    ];

    return buckets;
};

export const Dashboard: React.FC = () => {
    const [period, setPeriod] = useState<'day' | 'week' | 'month'>('week');
    const [stats, setStats] = useState<StatsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const loadStats = async (selectedPeriod: 'day' | 'week' | 'month') => {
        setLoading(true);
        setError('');
        try {
            const data = await healthAPI.getStats(selectedPeriod);
            setStats(data);
        } catch (err) {
            console.error('Error loading stats:', err);
            setError('Failed to load statistics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats(period);
    }, [period]);

    // Process Bristol Scale Trend data (must be before early returns)
    const bristolTrendData = useMemo(
        () => processBristolTrend(stats?.daily_stats || [], period),
        [stats, period]
    );

    // Process Health Score Trend data
    const healthScoreTrendData = useMemo(
        () => processHealthScoreTrend(stats?.daily_stats || [], period),
        [stats, period]
    );

    const avgHealthScore = useMemo(() => {
        if (!stats?.daily_stats || stats.daily_stats.length === 0) return 0;
        return calculatePeriodAverage(stats.daily_stats, 'health_score_value');
    }, [stats]);

    // Process Transit Time Distribution data
    const transitTimeData = useMemo(
        () => processTransitTimeDistribution(stats?.bathroom?.transit_time_distribution),
        [stats]
    );

    if (loading && !stats) {
        return <div className="loading">Loading dashboard...</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    // Process daily stats for charts
    const chartData = stats?.daily_stats.map(day => ({
        ...day,
        dateFormatted: new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
        healthScoreNum: day.health_score_value
    })) || [];

    // Process distribution data
    const circadianData = Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        label: i === 0 ? '12am' : i === 12 ? '12pm' : i > 12 ? `${i - 12}pm` : `${i}am`,
        count: stats?.time_of_day_distribution[i] || 0
    }));

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ background: '#1e1e1e', border: '1px solid #333', padding: '10px', borderRadius: '8px' }}>
                    <p style={{ color: '#fff', margin: '0 0 5px' }}>{label}</p>
                    {payload.map((p: any) => (
                        <p key={p.name} style={{ color: p.color, margin: 0, fontSize: '0.9rem' }}>
                            {p.name === 'bathroom_count' ? 'Bathroom Visits' :
                                p.name === 'avg_pain' ? 'Avg Pain' :
                                    p.name === 'max_pain' ? 'Max Pain' :
                                        p.name === 'meal_count' ? 'Meals' :
                                            p.name === 'healthScoreNum' ? 'Health Score' :
                                                p.name === 'psyllium_doses' ? 'Psyllium Doses' :
                                                    p.name === 'avg_bristol' ? 'Bristol Scale' :
                                                        p.name === 'score' ? 'Daily Score' :
                                                            p.name === 'movingAvg' ? '7-Day Avg' :
                                                                p.name}: {p.value !== null ? Math.round(p.value) : 'N/A'}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h2>Observability Dashboard</h2>
                <div className="period-selector">
                    {(['day', 'week', 'month'] as const).map((p) => (
                        <button
                            key={p}
                            className={`period-button ${period === p ? 'active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {stats && (
                <>
                    {/* KPI Cards */}
                    <div className="kpi-grid">
                        <div className="kpi-card">
                            <span className="kpi-label">Bathroom Frequency</span>
                            <span className="kpi-value">{stats.bathroom.daily_average}</span>
                            <span className="kpi-trend neutral">/ day avg</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Avg Pain (1-10)</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className={`kpi-value ${stats.bathroom.average_pain > 5 ? 'negative-text' : ''}`}>
                                    {stats.bathroom.average_pain}
                                </span>
                            </div>
                            <span className="kpi-trend">
                                {stats.bathroom.pain_events} events
                            </span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Avg Transit Time</span>
                            <span className="kpi-value">{stats.bathroom.average_transit_hours || '--'}</span>
                            <span className="kpi-trend">hours</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Avg Interval</span>
                            <span className="kpi-value">{stats.bathroom.interval_avg_hours || '--'}</span>
                            <span className="kpi-trend">hours between</span>
                        </div>
                        <div className="kpi-card">
                            <span className="kpi-label">Top Trigger</span>
                            <span className="kpi-value" style={{ fontSize: '1.2rem' }}>
                                {Object.keys(stats.meals.trigger_food_frequency)[0] || 'None'}
                            </span>
                            <span className="kpi-trend error-text">
                                {Object.values(stats.meals.trigger_food_frequency)[0] || 0} occurrences
                            </span>
                        </div>
                    </div>

                    {/* Main Charts */}
                    <div className="charts-section">
                        <div className="chart-card">
                            <h3>Health & Symptoms Trend</h3>
                            <div style={{ height: 300, width: '100%' }}>
                                <ResponsiveContainer>
                                    <ComposedChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis dataKey="dateFormatted" stroke="#888" />
                                        <YAxis yAxisId="left" stroke="#888" />
                                        <YAxis yAxisId="right" orientation="right" stroke="#888" domain={[0, 10]} hide />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar yAxisId="left" dataKey="bathroom_count" fill="#4a90e2" barSize={20} radius={[4, 4, 0, 0]} opacity={0.6} />
                                        <Line yAxisId="right" type="monotone" dataKey="avg_pain" stroke="#ff5252" strokeWidth={2} dot={{ r: 3 }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="chart-card">
                            <h3>📊 Bristol Scale Trend</h3>
                            {bristolTrendData.length > 0 ? (
                                <div style={{ height: 300, width: '100%' }}>
                                    <ResponsiveContainer>
                                        <ComposedChart data={bristolTrendData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                            <XAxis dataKey="dateFormatted" stroke="#888" />
                                            <YAxis domain={[1, 7]} ticks={[1, 2, 3, 4, 5, 6, 7]} stroke="#888" />
                                            <Tooltip content={<CustomTooltip />} />

                                            {/* Ideal zone shading (Bristol 3-4) */}
                                            <ReferenceArea y1={3} y2={4} fill="#e8f5e9" fillOpacity={0.3} />

                                            <Line
                                                type="monotone"
                                                dataKey="avg_bristol"
                                                stroke="#66bb6a"
                                                strokeWidth={2}
                                                dot={{ r: 4 }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
                                    No Bristol scale data available for this period
                                </div>
                            )}
                        </div>

                        <div className="chart-card">
                            <h3>📈 Health Score Trend</h3>
                            <div style={{ height: 300, width: '100%' }}>
                                <ResponsiveContainer>
                                    <LineChart data={healthScoreTrendData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis dataKey="dateFormatted" stroke="#888" />
                                        <YAxis domain={[0, 100]} stroke="#888" />
                                        <Tooltip content={<CustomTooltip />} />

                                        {/* Period average reference line */}
                                        <ReferenceLine
                                            y={avgHealthScore}
                                            stroke="#888"
                                            strokeDasharray="3 3"
                                            label={{ value: 'Avg', position: 'right', fill: '#888' }}
                                        />

                                        {/* Daily score */}
                                        <Line
                                            type="monotone"
                                            dataKey="score"
                                            stroke="#4a90e2"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                            name="Daily Score"
                                        />

                                        {/* 7-day moving average */}
                                        <Line
                                            type="monotone"
                                            dataKey="movingAvg"
                                            stroke="#4a90e2"
                                            strokeWidth={1}
                                            strokeDasharray="5 5"
                                            dot={false}
                                            opacity={0.6}
                                            name="7-Day Avg"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="chart-card">
                            <h3>Meal Volume</h3>
                            <div style={{ height: 300, width: '100%' }}>
                                <ResponsiveContainer>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis dataKey="dateFormatted" stroke="#888" hide />
                                        <YAxis stroke="#888" />
                                        <Tooltip cursor={{ fill: 'transparent' }} />
                                        <Bar dataKey="meal_count" fill="#66bb6a" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="chart-card">
                            <h3>🕒 Daily Routine (24h)</h3>
                            <div style={{ height: 250, width: '100%' }}>
                                <ResponsiveContainer>
                                    <BarChart data={circadianData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis dataKey="label" stroke="#888" interval={3} />
                                        <YAxis stroke="#888" allowDecimals={false} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1e1e1e', borderColor: '#333', color: '#fff' }}
                                            cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                        />
                                        <Bar dataKey="count" fill="#ab47bc" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="chart-card">
                            <h3>⏱️ Transit Time Distribution</h3>
                            {transitTimeData.length > 0 && transitTimeData.some(b => b.count > 0) ? (
                                <div style={{ height: 250, width: '100%' }}>
                                    <ResponsiveContainer>
                                        <BarChart data={transitTimeData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                            <XAxis dataKey="range" stroke="#888" />
                                            <YAxis stroke="#888" allowDecimals={false} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#1e1e1e', borderColor: '#333', color: '#fff' }}
                                                cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                            />

                                            {/* Normal zone shading (18-30h) */}
                                            <ReferenceArea x1="18-24h" x2="30-36h" fill="#e8f5e9" fillOpacity={0.3} />

                                            <Bar dataKey="count" fill="#ab47bc" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
                                    No transit time data available for this period
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="insights-grid">
                        <div className="chart-card">
                            <h3>⚠️ Top Trigger Foods</h3>
                            {Object.keys(stats.meals.trigger_food_frequency).length > 0 ? (
                                <ul className="insight-list">
                                    {Object.entries(stats.meals.trigger_food_frequency).map(([food, count]) => (
                                        <li key={food} className="insight-item">
                                            <span className="insight-name">{food}</span>
                                            <span className="insight-count" style={{ color: '#ff5252' }}>{count}x</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div style={{ color: '#666', padding: '10px 0' }}>No trigger foods recorded in this period</div>
                            )}
                        </div>

                        <div className="chart-card">
                            <h3>🍽️ Most Common Foods</h3>
                            {stats.meals.most_common_foods.length > 0 ? (
                                <ul className="insight-list">
                                    {stats.meals.most_common_foods.map(({ food, count }) => (
                                        <li key={food} className="insight-item">
                                            <span className="insight-name">{food}</span>
                                            <span className="insight-count">{count}x</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div style={{ color: '#666', padding: '10px 0' }}>No meals recorded</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
